// src/components/CampaignSubscribers.tsx
import './CampaignSubscribers.css'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getUserPlanLimits, hasReachedLimit } from '../config/planLimits'

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'

interface Subscriber {
  id: string | number
  name: string
  email: string
}

interface Notification {
  id: number
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
}

interface CampaignSubscribersProps {
  campaign: {
    id: string | number
    [key: string]: any
  }
  onClose: () => void
  onUpgrade?: () => void
}

interface User {
  selectedPlan?: string
  plan?: string
  [key: string]: any
}

const EXPORT_LIMITS: Record<'starter' | 'pro' | 'premium', number> = {
  starter: 5,
  pro: 20,
  premium: Infinity
}

export default function CampaignSubscribers({ campaign, onClose, onUpgrade }: CampaignSubscribersProps) {
  const { user } = useAuth() as { user: User | null }

  const isMountedRef = useRef(true)
  const abortControllerRef = useRef<AbortController | null>(null)
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [subscribers, setSubscribers] = useState<Subscriber[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [exportCountToday, setExportCountToday] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [isDeleting, setIsDeleting] = useState<string | number | null>(null)

  const rawPlan = user?.selectedPlan || user?.plan || 'starter'
  
  const userPlan = useMemo(() => {
    const normalized = rawPlan.toLowerCase()
    if (normalized === 'enterprise' || normalized === 'premium') return 'premium'
    if (normalized === 'professional' || normalized === 'pro') return 'pro'
    return 'starter'
  }, [rawPlan])

  const exportLimit = useMemo(() => EXPORT_LIMITS[userPlan] || EXPORT_LIMITS.starter, [userPlan])
  const canExport = useMemo(() => !hasReachedLimit(exportCountToday, exportLimit), [exportCountToday, exportLimit])

  const planDisplayName = useMemo(() => {
    const names: Record<string, string> = { starter: 'Starter', pro: 'Professional', premium: 'Enterprise' }
    return names[userPlan] || 'Starter'
  }, [userPlan])

  const filteredSubscribers = useMemo(() => {
    if (!searchTerm.trim()) return subscribers
    const term = searchTerm.toLowerCase()
    return subscribers.filter(s => s.name?.toLowerCase().includes(term) || s.email?.toLowerCase().includes(term))
  }, [searchTerm, subscribers])

  const addNotification = useCallback((type: Notification['type'], message: string) => {
    const id = Date.now()
    setNotifications(prev => [...prev, { type, message, id }])
    if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current)
    notificationTimeoutRef.current = setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id))
    }, 4000)
  }, [])

  // ================= FETCH SUBSCRIBERS =================
  const fetchSubscribers = useCallback(async () => {
  setLoading(true)

  try {
    const token = localStorage.getItem('accessToken')
    const res = await fetch(`${API_URL}/campaigns/${campaign.id}/subscribers`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })

    if (res.ok) {
      const data = await res.json()
      setSubscribers(data.data || data.subscribers || [])
    } else {
      setSubscribers([])
    }
  } catch (err) {
    setSubscribers([])
  } finally {
    setLoading(false)
  }
}, [campaign.id])
  // ================= FETCH EXPORT COUNT =================
  const fetchExportCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken')
      const res = await fetch(`${API_URL}/campaigns/${campaign.id}/export-count`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      })
      if (res.ok) {
        const data = await res.json()
        setExportCountToday(data.data?.totalExports || 0)
      }
    } catch (error) {
      console.error('Failed to fetch export count:', error)
    }
  }, [campaign.id])

  // ================= EFFECT - RUN ONCE =================
  useEffect(() => {
    fetchSubscribers()
    fetchExportCount()

    return () => {
      isMountedRef.current = false
      if (abortControllerRef.current) abortControllerRef.current.abort()
      if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current)
    }
  }, []) // ✅ Empty array - runs once

  // ================= DELETE SUBSCRIBER =================
  const handleDeleteSubscriber = async (id: string | number) => {
    setIsDeleting(id)
    const prev = [...subscribers]
    setSubscribers(prev.filter(s => s.id !== id))

    try {
      const token = localStorage.getItem('accessToken')
      const res = await fetch(`${API_URL}/campaigns/${campaign.id}/subscribers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      })

      if (!res.ok) throw new Error('Delete failed')
      addNotification('success', 'Subscriber deleted successfully')
    } catch (err) {
      setSubscribers(prev)
      addNotification('error', 'Delete failed')
    } finally {
      setIsDeleting(null)
    }
  }

  // ================= EXPORT CSV =================
  const handleExportCSV = async () => {
    if (!canExport) {
      addNotification('warning', `Daily export limit reached (${exportCountToday}/${exportLimit})`)
      onUpgrade?.()
      return
    }

    setIsExporting(true)
    let url: string | null = null

    try {
      const headers = 'Name,Email\n'
      const csv = headers + filteredSubscribers.map(s => `"${s.name || ''}","${s.email || ''}"`).join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = `subscribers-${campaign.id}-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      setExportCountToday(prev => prev + 1)
      addNotification('success', `Export successful!`)
    } catch (err) {
      addNotification('error', 'Export failed')
    } finally {
      if (url) URL.revokeObjectURL(url)
      setIsExporting(false)
    }
  }

  // ================= RENDER =================
  if (loading) {
    return (
      <div className="modal">
        <div className="container">
          <div className="loading">Loading subscribers...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal">
      <div className="container">
        <div className="modal-header">
          <h2>Subscribers ({filteredSubscribers.length})</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className={`plan-badge plan-${userPlan}`}>
          <span>{planDisplayName} Plan</span>
        </div>

        {notifications.length > 0 && (
          <div className="notifications-container">
            {notifications.map(({ id, type, message }) => (
              <div key={id} className={`notification notification-${type}`}>{message}</div>
            ))}
          </div>
        )}

        <div className="controls">
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="search-input" />
          <div className="export-control">
            <button onClick={handleExportCSV} disabled={isExporting || !canExport} className={`export-button ${!canExport ? 'disabled' : ''}`}>
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <span className="export-limit">{exportLimit === Infinity ? 'Unlimited' : `${exportCountToday}/${exportLimit}`}</span>
          </div>
        </div>

        <div className="subscribers-list">
          {filteredSubscribers.length === 0 ? (
            <div className="no-results">{searchTerm ? 'No matches' : 'No subscribers yet'}</div>
          ) : (
            <ul>
              {filteredSubscribers.map((s) => (
                <li key={s.id} className="subscriber-item">
                  <div className="subscriber-info">
                    <span className="subscriber-name">{s.name || 'No name'}</span>
                    <span className="subscriber-email">{s.email}</span>
                  </div>
                  <button onClick={() => handleDeleteSubscriber(s.id)} disabled={isDeleting === s.id} className="delete-button">
                    {isDeleting === s.id ? 'Deleting...' : 'Delete'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="close-btn">Close</button>
        </div>
      </div>
    </div>
  )
}